<template>
    <div class="grid-view">
        <ResizeSettingsModal
            v-if="resizeSettingsModalIsVisible"
            :editable="editable"
            :resize-settings="resizeSettingsToEdit"
            :is-group-resize-settings="isEditingGroupResizeSettings"
            :is-single-template-resize-settings="isEditingSingleTemplateResizeSettings"
            :preview-image-url="imageToResize"
            :template-ids="templateIdsToEditResizeSettingsOf"
            @setResizeSettings="setResizeSettings"
            @close="toggleResizeSettingsModal"
        />
        <component
            :is="componentName"
            :filetype="editable.type"
            :label="EditableGroupScopeLabel"
            :media-item="getOverwriteMediaItem(editable, groupsAffectingEditable, null)"
            :model-value="getMediaItemUrl(editable, groupsAffectingEditable, null)"
            @click="fileSelected(editable, groupsAffectingEditable, null)"
            @iconClick="fileSelected(editable, groupsAffectingEditable, null)"
            @openResizeSettings="showResizeSettingsModal(templatesUsingGroupMediaItem, true, false)"
            @openEditableSettings="onOpenEditableSettings(editable, groupsAffectingEditable, null)"
        />
        <component
            :is="componentName"
            v-for="(label, templateId) in templateLabels"
            v-show="isOpened"
            :key="getForKey(editable, templateId)"
            :editable="editable"
            :filetype="editable.type"
            :label="label"
            :label-color="getLabelColor(editable, groupsAffectingEditable, templateId)"
            :media-item="getOverwriteMediaItem(editable, groupsAffectingEditable, templateId)"
            :template-id="templateId"
            :model-value="getMediaItemUrl(editable, groupsAffectingEditable, templateId)"
            @click="fileSelected(editable, groupsAffectingEditable, templateId)"
            @iconClick="fileSelected(editable, groupsAffectingEditable, templateId)"
            @openResizeSettings="
                showResizeSettingsModal(
                    [templateId],
                    !isTemplateOverwrite(editable, groupsAffectingEditable, templateId),
                    true
                )
            "
            @openEditableSettings="onOpenEditableSettings(editable, groupsAffectingEditable, templateId)"
        />
    </div>
</template>

<script lang="ts">
import { EditableType, LegacyEditableTypes } from "hoxton-shared/enums/editables";
import getMediaItemResizedUrl from "hoxton-shared/utils/getMediaItemResizedUrl";

import EditableImageThumbnail from "@/components/Campaign/Editables/EditableImageThumbnail.vue";
import EditableVideoThumbnail from "@/components/Campaign/Editables/EditableVideoThumbnail.vue";
import ResizeSettingsModal from "@/components/ResizeSettingsModal.vue";
import { CampaignGetters } from "@/store/modules/campaign";
import { EditorAction } from "@/store/modules/editor";
import { EditableGroupScopeLabel } from "@/enums/overwrites";
import config from "@/config";
import editableUtils from "@/utils/editables";
import { defineComponent, toRef, type PropType } from "vue";
import { useOverwrite } from "@/composables/useOverwrite";
import { useEditableFile } from "@/composables/useEditableFile";
import { useEditableMethods } from "@/composables/useEditableMethods";
import type { Editable, EditableType as EditableTypeType, HTML5MasterTemplate } from "hoxton-types";
import { useEditable } from "@/composables/useEditable";
import { useMasterTemplates } from "@/composables/useMasterTemplates";

export default defineComponent({
    name: "EditableFileGridView",
    components: {
        EditableVideoThumbnail,
        EditableImageThumbnail,
        ResizeSettingsModal
    },
    props: {
        editable: {
            type: Object as PropType<Editable>,
            required: true
        },

        groupsAffectingEditable: {
            type: Array as PropType<string[]>,
            required: true
        },
        type: {
            type: String as PropType<EditableTypeType>,
            default: EditableType.Image
        },
        isOpened: {
            type: Boolean,
            default: true
        }
    },
    setup(props, { emit }) {
        const type = toRef(props, "type");
        const editable = toRef(props, "editable");
        const groupsAffectingEditable = toRef(props, "groupsAffectingEditable");

        const { masterTemplates } = useMasterTemplates();
        const { templateLabels } = useEditable({ editable });
        const { getOverwriteMediaItem, isTemplateOverwrite } = useOverwrite();
        const { getForKey, getLabelColor } = useEditableMethods({ emit });
        const {
            resizeSettingsModalIsVisible,
            toggleResizeSettingsModal,
            fileSelected,
            showResizeSettingsModal,
            imageToResize,
            resizeSettingsToEdit,
            isEditingGroupResizeSettings,
            isEditingSingleTemplateResizeSettings,
            templateIdsToEditResizeSettingsOf,
            setResizeSettings,
            templatesUsingGroupMediaItem
        } = useEditableFile({
            type,
            editable,
            groupsAffectingEditable,
            emit
        });

        return {
            templateLabels,
            getForKey,
            getLabelColor,
            getOverwriteMediaItem,
            isTemplateOverwrite,
            resizeSettingsModalIsVisible,
            toggleResizeSettingsModal,
            fileSelected,
            showResizeSettingsModal,
            imageToResize,
            resizeSettingsToEdit,
            isEditingGroupResizeSettings,
            isEditingSingleTemplateResizeSettings,
            templateIdsToEditResizeSettingsOf,
            setResizeSettings,
            templatesUsingGroupMediaItem,
            masterTemplates
        };
    },
    data() {
        return {
            EditableGroupScopeLabel
        };
    },
    computed: {
        componentName() {
            switch (this.type) {
                case EditableType.Video:
                    return "editable-video-thumbnail";

                case LegacyEditableTypes.BackgroundImage:
                case EditableType.Image:
                case EditableType.Folder:
                default:
                    return "editable-image-thumbnail";
            }
        }
    },

    methods: {
        getMediaItemUrl(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
            const mediaItem = this.getOverwriteMediaItem(editable, groupsAffectingEditable, templateId);
            if (this.type === EditableType.Folder && !mediaItem) {
                const template = this.$store.getters[CampaignGetters.templateById](templateId);
                if (template) {
                    const defaultValue = editableUtils.getDefaultValue(editable, templateId).replace(/^\/|\/$/g, "");
                    // In sandbox mode the preview has index.html in it so that the local server can load the template
                    //  we do not want index.html in the path so we replace it out here
                    return `${template.preview.replace(/index.html?$/, "")}/${defaultValue}/static.png`;
                }
            }

            if (mediaItem) {
                const masterTemplate = templateId ? (this.masterTemplates[templateId] as HTML5MasterTemplate) : null;
                const renderService = import.meta.env.RENDERER_SERVICE ?? config.appConfig.RENDERER_SERVICE;

                return getMediaItemResizedUrl({
                    editable,
                    mediaItem,
                    masterTemplate,
                    renderService
                });
            }

            return editableUtils.getDefaultValue(editable, templateId);
        },

        onOpenEditableSettings(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
            this.$store.dispatch(EditorAction.SetEditedEditable, {
                editable,
                groupsAffectingEditable,
                masterTemplateId: templateId
            });

            this.$store.dispatch(EditorAction.SetSettingsPaneVisibility, true);
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.grid-view {
    background-color: $cmp-dark-secondary-sidebar-color;
    display: flex !important;
    flex-wrap: wrap;
    justify-content: space-around;
    align-items: center;
    width: 100%;
    flex: 1 0 100%;

    .editable-image-thumbnail {
        display: flex;
        margin: 5px 1px;
    }
}
</style>
